import { nateraIntakeActivateUrl, onboardingUrl } from "@medbillai/utils/urls";

import type { User } from "../../../lib/auth/authState";

/**
 * Where an authenticated navigation must be sent to resume an unfinished flow,
 * or `null` to let it through.
 *
 * Acts on the latest reads, trusted as-is (the single-session assumption): a
 * stale fire costs one redirect that the destination's own network-only reads
 * correct, and `undefined` — a read that has not landed, or one that failed —
 * fails open.
 *
 * Terms are read directly, not inferred from identity presence: `createPerson`
 * has callers outside onboarding. No feature flags — a LaunchDarkly variation
 * can re-roll between reads.
 */
export function onboardingGate({
  user,
  pathname,
  hasPendingNateraIntake,
}: {
  user: User | null | undefined;
  pathname: string;
  hasPendingNateraIntake: boolean | undefined;
}): string | null {
  // A flow's whole subtree is exempt — a user already deeper in it must not be
  // bounced sideways — but only slash-delimited: siblings like /onboarding-help
  // stay gated.
  const resume = (destination: string) =>
    pathname === destination || pathname.startsWith(`${destination}/`)
      ? null
      : destination;

  // An admin impersonating a member must not enter the target's flows.
  if (user?.isShadow) {
    return null;
  }
  // Ahead of onboarding: activation collects the same terms and identity, and
  // is the only screen that turns the held answers into the member's case.
  if (hasPendingNateraIntake) {
    return resume(nateraIntakeActivateUrl());
  }
  if (!user?.partner) {
    return null;
  }
  // Strictly `null`: `undefined` means unread, not unaccepted.
  return user.acceptedTermsAt === null ? resume(onboardingUrl()) : null;
}
